import React, { useEffect } from 'react'
import {
  FocusContext,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { useNavigate } from 'react-router'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import { useAppDispatch, useAppSelector } from '../../hooks/useStore'
import { getErrorConfig } from './util'
import { exitApp, triggerSentryError } from '../../../../shared/helpers.js'
import tts from '../../lib/TTS.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'

const TRY_AGAIN_FOCUS_KEY = 'try-again-button'
const GO_BACK_FOCUS_KEY = 'go-back-button'
const EXIT_FOCUS_KEY = 'exit-button'
const TRY_AGAIN_BUTTON_TEXT = 'Try Again'
const EXIT_BUTTON_TEXT = 'Exit'

declare const COMPILING_FOR_COMCASTX1: boolean

function ErrorScreenTS() {
  const { focusKey, ref } = useFocusable()
  const navigate = useNavigate()
  const errorState = useAppSelector((state) => state.error)
  const dispatch = useAppDispatch()
  const error = getErrorConfig(errorState)

  useEffect(() => {
    ttsScreen()
    googleAnalytics4.sendPageViewEvent('Error Screen - Message')

    triggerSentryError(
      `Error: ${errorState.code ?? 'Unknown error code'} (${
        errorState.status ?? 'Unknown error status'
      })`,
      'ErrorScreen.js',
      {
        error: errorState.error,
        errorLocation: 'ErrorScreen component',
        errorType: errorState.errorType,
      },
      true
    )

    dispatch({
      type: 'DEEPLINK_URL',
      payload: { url: '', force: false },
    })

    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  const ttsScreen = () => {
    tts.pushTextToRead(
      `${error.errorTitle} ${error.errorText} ${
        COMPILING_FOR_COMCASTX1
          ? 'Need more help?. Visit pbs.org slash pbs comcast help.'
          : ''
      }`
    )
  }

  const tryAgain = () => {
    const {errorPath} = errorState.content
    if (errorPath) {
      navigate(errorPath, { replace: true })
    }
  }

  const goBack = () => {
    if (errorState.status === 'fatal') {
      window.location.href = '/'
    } else if (window.history.length < 3) {
      navigate('/')
    } else {
      navigate(-1)
    }
  }

  useKeyDown(
    KeyTypeEnum.Back,
    goBack,
    { stopPropagation: true }
  )

  const onFocusHandler = (label: string) => {
    tts.stop()
    tts.addTextToRead(`Focused on ${label} button`)
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className="error-screen__container">
        <div className="error-screen__text-container">
          <h1 className="error-screen__title">{error.errorTitle}</h1>
          <p className="error-screen__text">{error.errorText}</p>
        </div>

        <div className="error-screen__button-container">
          {error.shouldDisplayTryAgainButton && (
            <FocusableButtonTS
              focusKey={TRY_AGAIN_FOCUS_KEY}
              stealFocus
              onEnterPress={tryAgain}
              onFocus={() => onFocusHandler(TRY_AGAIN_BUTTON_TEXT)}
            >
              {TRY_AGAIN_BUTTON_TEXT}
            </FocusableButtonTS>
          )}
          {error.shouldDisplayGoBackButton && (
            <FocusableButtonTS
              focusKey={GO_BACK_FOCUS_KEY}
              stealFocus={!error.shouldDisplayTryAgainButton}
              onEnterPress={goBack}
              onFocus={() => onFocusHandler(error.goBackButtonText)}
            >
              {error.goBackButtonText}
            </FocusableButtonTS>
          )}
          {error.shouldDisplayExitButton && (
            <FocusableButtonTS
              focusKey={EXIT_FOCUS_KEY}
              stealFocus={
                !error.shouldDisplayTryAgainButton &&
                !error.shouldDisplayGoBackButton
              }
              onEnterPress={exitApp}
              onFocus={() => onFocusHandler(EXIT_BUTTON_TEXT)}
            >
              {EXIT_BUTTON_TEXT}
            </FocusableButtonTS>
          )}
        </div>
        {COMPILING_FOR_COMCASTX1 && (
          <div
            style={{
              fontSize: '32px',
              marginTop: '40px',
              textAlign: 'center',
              lineHeight: '1.4',
            }}
          >
            <p>Need more help?</p>
            <p>pbs.org/pbscomcasthelp</p>
          </div>
        )}
      </div>
    </FocusContext.Provider>
  )
}

export default ErrorScreenTS
